import React, {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'
import {Divider} from '../Divider'

const INDICATOR_TRANSITION_DURATION = 300
const INDICATOR_HEIGHT = 2
const SCROLL_CONTEXT_MARGIN_PX = 48

const getDistanceToRevealTab = (
  tabRect: DOMRect,
  stripRect: DOMRect
): number => {
  if (tabRect.left < stripRect.left) {
    return tabRect.left - stripRect.left - SCROLL_CONTEXT_MARGIN_PX
  }
  if (tabRect.right > stripRect.right) {
    return tabRect.right - stripRect.right + SCROLL_CONTEXT_MARGIN_PX
  }
  return 0
}

const scrollStripToRevealTab = (strip: HTMLElement, tab: HTMLElement) => {
  const distance = getDistanceToRevealTab(
    tab.getBoundingClientRect(),
    strip.getBoundingClientRect()
  )
  if (distance === 0) return
  strip.scrollTo({left: strip.scrollLeft + distance, behavior: 'smooth'})
}

export type TabsVariant = 'scrollable' | 'fullWidth'
export type TabsMode = 'tabs' | 'navigation'

interface TabsContextValue {
  activeTab: string
  onTabChange: (value: string) => void
  variant: TabsVariant
  mode: TabsMode
  registerTab: (value: string, element: HTMLButtonElement) => void
  unregisterTab: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

export const useTabsContext = () => {
  const context = useContext(TabsContext)
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component')
  }
  return context
}

interface TabsProps<TabId> {
  /** Callback when tab changes */
  onChange?: (value: TabId) => void
  /** Tabs variant - scrollable or fullWidth */
  variant?: TabsVariant
  /** Tabs mode - tabs (default) or navigation */
  mode?: TabsMode
  /** Active tab value (for navigation mode) */
  activeTab?: TabId
  /** Whether to show divider on the bottom of Tabs */
  withDivider?: boolean
  /** Callback when tab is clicked in navigation mode */
  onTabClick?: (value: TabId) => void
  /** Tab components as children */
  children: ReactNode
  /** Additional CSS class */
  className?: string
  /** Data ID for testing */
  dataId?: string
  /** ARIA label for accessibility */
  'aria-label'?: string
  /** Inverted styles for dark backgrounds */
  inverse?: boolean
}

const TabsContainer = styled.div`
  position: relative;
  width: 100%;
`

const TabsList = styled.div`
  display: flex;
  position: relative;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hack for the focus ring on first element */
  padding-left: ${cssTheme.layout.spacing.s200};
  padding-right: ${cssTheme.layout.spacing.s200};
  margin-left: ${minus(cssTheme.layout.spacing.s200)};
  margin-right: ${minus(cssTheme.layout.spacing.s200)};

  &.scrollable {
    overflow-x: auto;
    gap: ${cssTheme.layout.spacing.s600};

    ${linariaMq.desktopXs} {
      gap: ${cssTheme.layout.spacing.s800};
    }
  }

  &.fullWidth {
    overflow-x: hidden;
    gap: 0;
  }
`
const DividerWrapper = styled.div<{inverse: boolean}>`
  margin-top: -1px;
  opacity: ${props => (props.inverse ? 0.5 : 1)};
`

const ActiveIndicator = styled.div<{inverse: boolean}>`
  position: absolute;
  bottom: 0;
  height: ${INDICATOR_HEIGHT}px;
  background-color: ${props =>
    props.inverse
      ? cssTheme.colors.tabs.inverse.selected.border
      : cssTheme.colors.tabs.default.selected.border};
  border-radius: ${cssTheme.layout.radius.rounded};
  transition: all ${INDICATOR_TRANSITION_DURATION}ms
    cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1;
`

export function Tabs<TabId>(props: TabsProps<TabId>): JSX.Element
export function Tabs(props: TabsProps<string>): JSX.Element
// eslint-disable-next-line react/function-component-definition
export function Tabs<TabId extends string>({
  onChange,
  variant = 'scrollable',
  mode = 'tabs',
  activeTab,
  onTabClick,
  children,
  className,
  withDivider = true,
  dataId,
  'aria-label': ariaLabel,
  inverse = false
}: TabsProps<TabId>) {
  const [tabElements, setTabElements] = useState<
    Map<string, HTMLButtonElement>
  >(new Map())
  const [indicatorStyle, setIndicatorStyle] = useState<{
    left: number
    width: number
    animate: boolean
  }>({left: 0, width: 0, animate: true})
  const tabsListRef = useRef<HTMLDivElement>(null)

  const registerTab = useCallback(
    (value: string, element: HTMLButtonElement) => {
      setTabElements(prev => new Map(prev).set(value, element))
    },
    []
  )

  const unregisterTab = useCallback((value: string) => {
    setTabElements(prev => {
      const newMap = new Map(prev)
      newMap.delete(value)
      return newMap
    })
  }, [])

  const updateIndicatorPosition = useCallback(
    (animate: boolean) => {
      const activeElement = activeTab ? tabElements.get(activeTab) : null

      if (activeElement) {
        // offsetLeft shares the TabsList origin with the indicator's absolute
        // `left`, unlike viewport rects, so it holds in LTR and RTL alike
        const left = activeElement.offsetLeft
        const width = activeElement.offsetWidth

        setIndicatorStyle(prev => {
          if (prev.left !== left || prev.width !== width) {
            return {left, width, animate}
          }
          return prev
        })
      }
    },
    [activeTab, tabElements]
  )

  const syncWithActiveTab = useCallback(
    (options?: {animate?: boolean}) => {
      updateIndicatorPosition(options?.animate ?? true)

      const isNavigationStrip =
        mode === 'navigation' && variant === 'scrollable'
      if (!isNavigationStrip) return
      const activeElement = activeTab ? tabElements.get(activeTab) : undefined
      const tabsList = tabsListRef.current
      if (!activeElement || !tabsList) return
      scrollStripToRevealTab(tabsList, activeElement)
    },
    [updateIndicatorPosition, mode, variant, activeTab, tabElements]
  )

  useEffect(() => {
    const syncAfterTabsHaveLaidOut = setTimeout(syncWithActiveTab, 1)
    return () => clearTimeout(syncAfterTabsHaveLaidOut)
  }, [syncWithActiveTab])

  const syncWithActiveTabRef = useRef(syncWithActiveTab)
  syncWithActiveTabRef.current = syncWithActiveTab

  useEffect(() => {
    const tabsList = tabsListRef.current
    if (!tabsList || typeof ResizeObserver === 'undefined') return
    const resyncWithoutAnimation = () =>
      syncWithActiveTabRef.current({animate: false})
    const observer = new ResizeObserver(resyncWithoutAnimation)
    observer.observe(tabsList)
    return () => observer.disconnect()
  }, [])

  const handleTabChange = useCallback(
    (tabValue: string) => {
      if (mode === 'navigation') {
        onTabClick?.(tabValue as TabId)
      } else {
        onChange?.(tabValue as TabId)
      }
    },
    [mode, onTabClick, onChange]
  )

  const contextValue: TabsContextValue = useMemo(
    () => ({
      activeTab: activeTab || '',
      onTabChange: handleTabChange,
      variant,
      mode,
      registerTab,
      unregisterTab
    }),
    [activeTab, handleTabChange, variant, mode, registerTab, unregisterTab]
  )

  return (
    <TabsContext.Provider value={contextValue}>
      <TabsContainer className={className} data-id={dataId}>
        <TabsList
          ref={tabsListRef}
          className={variant}
          role="tablist"
          aria-label={ariaLabel}
        >
          {Children.map(children, child => {
            if (isValidElement(child)) {
              return cloneElement(child, {
                ...child.props,
                variant,
                inverse
              })
            }
            return child
          })}
          <ActiveIndicator
            inverse={inverse}
            style={{
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
              ...(indicatorStyle.animate ? {} : {transition: 'none'})
            }}
          />
        </TabsList>
        {withDivider && (
          <DividerWrapper inverse={inverse}>
            <Divider />
          </DividerWrapper>
        )}
      </TabsContainer>
    </TabsContext.Provider>
  )
}
